import React, { useMemo, useRef, useState } from "react";
import MaterialReactTable from "material-react-table";
import {
  Paper,
  Box,
  Button,
  MenuItem,
  IconButton,
  Dialog,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  ViewPatient,
  AddPatient, 
  useGetPatients,
} from "../../components/PatientComponents";
import { useAuthHeader, useAuthUser } from "react-auth-kit";
import { getPatientsColumns } from "./PatientsColumns";
import { LoadingPaper, LoadingError } from "../../components/LoadingComponents";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useDeletePatient } from "../../components/PatientComponents/usePatientQueries";
import { useLocation } from "react-router";

function Patients(props) {
  const filter = useLocation()?.state || null;

  const authUser = useAuthUser();
  const authHeader = useAuthHeader();
  const [initialState, setInitialState] = useState({});

  // === web service queries/mutations ===
  const activeFilter = filter?.is_active ?? true;
  const patientsQuery = useGetPatients({ active: activeFilter });
  const patientsDeleteMutation = useDeletePatient();

  // === AddPatient modal handling ===
  const [openAddPatient, setOpenAddPatient] = useState(false);
  const handleOpenAddPatient = () => {
    setOpenAddPatient(true);
  };
  const handleCloseAddPatient = () => {
    setOpenAddPatient(false);
  };

  // === ViewPatient modal handling ===
  const [openViewPatient, setOpenViewPatient] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedPatientDoctor, setSelectedPatientDoctor] = useState(null);
  const openInEditMode = useRef(false);

  const handleOpenViewPatient = (patient_id, org_id, doctor_id) => {
    setSelectedPatient(patient_id);
    setSelectedOrg(org_id);
    if (doctor_id != 0){
          setSelectedPatientDoctor(doctor_id);
    }
    
    setOpenViewPatient(true);
  };
  const handleCloseViewPatient = () => {
    setOpenViewPatient(false);
  };

  const isDoctor = authUser()?.role === 3;

  const tableColumns = useMemo(
    () =>
      getPatientsColumns({
        showDoctor: !isDoctor,
        showOrg: authUser()?.role === 1,
      }),
    [isDoctor, authUser()?.role]
  );

  const columnFilters = [];
  if (filter !== null) {
    columnFilters.push({
      id: "is_active",
      value: filter.is_active,
    });
  }

  const patients = patientsQuery.data?.patients ?? [];

  return (
    <main style={{ width: "100%" }}>
      <Dialog open={openAddPatient}>
        <AddPatient closeModal={handleCloseAddPatient} />
      </Dialog>

      <Dialog open={openViewPatient} fullScreen>
        <ViewPatient
          _editMode={openInEditMode.current}
          patient_id={selectedPatient}
          org_id={selectedOrg}
          doctor_id={selectedPatientDoctor}
          closeModal={handleCloseViewPatient}
        />
      </Dialog>

      <PageTitle title="Pazienti" />
      <Paper
        style={{ minHeight: "fitContent", margin: "3vh", maxWidth: "96%" }}
      >
        <Box>
          <Button
            style={{ margin: "15px" }}
            color="primary"
            onClick={handleOpenAddPatient}
            variant="contained"
          >
            AGGIUNGI PAZIENTE
          </Button>
        </Box>
        {patientsQuery.isLoading ? (
          <LoadingPaper />
        ) : patientsQuery.isError ? (
          <LoadingError error={patientsQuery.error} />
        ) : (
          patientsQuery.isSuccess && (
            <MaterialReactTable
              columns={tableColumns}
              data={patients.map((el) => ({ ...el }))}
              initialState={{
                density: "compact",
                showColumnFilters: true,
                columnFilters,
              }}
              enablePagination={true}
              enableBottomToolbar={true}
              enableTopToolbar={false}
              enableSorting={true}
              muiTablePaperProps={{
                elevation: 0,
              }}
              enableColumnResizing
              enableFacetedValues={patients.length > 0}
              enableColumnActions={false}
              enableRowSelection={false}
              muiTableContainerProps={{
                sx: {
                  overflowX: "auto",
                },
              }}
              enableRowActions={true}
              displayColumnDefOptions={{
                "mrt-row-actions": { header: "Azioni" },
              }}
              positionActionsColumn="last"
              renderRowActionMenuItems={({ closeMenu, row, table }) => {
                return [
                <MenuItem
                  key="view"
                  onClick={() => {
                    openInEditMode.current = false;
                    let doctorid =  row.original.doctor_id ? row.original.doctor_id : 0
                    handleOpenViewPatient(
                      row.original.id,
                      row.original.org_id,
                      doctorid
                    );
                    closeMenu();
                  }}
                >
                  <IconButton>
                    <ViewIcon color="primary" />
                  </IconButton>
                  Visualizza paziente
                </MenuItem>,

                <MenuItem
                  key="edit"
                  onClick={() => {
                    openInEditMode.current = true;
                    handleOpenViewPatient(
                      row.original.id,
                      row.original.org_id,
                      row.original.doctor_id
                    );
                    closeMenu();
                  }}
                >
                  <IconButton>
                    <EditIcon color="warning" />
                  </IconButton>
                  Modifica paziente
                </MenuItem>,

                <MenuItem
                  key="delete"
                  onClick={() => {
                    setSelectedPatient(row.original.id);
                    patientsDeleteMutation.mutate(row.original.id);
                    closeMenu();
                  }}
                >
                  <IconButton>
                    <DeleteIcon color="error" />
                  </IconButton>
                  Dismetti paziente
                </MenuItem>,
              ];}}
            />
          )
        )}
      </Paper>
    </main>
  );
}

export default Patients;
